import { useEffect } from 'react';

import { useEnableLoyaltyServiceRewards } from 'features/reward/hooks/use-enable-loyalty-service-rewards';
import { useLoyaltyServiceReward } from 'features/reward/hooks/use-loyalty-service-reward';
import { useServiceRewardsCatalog } from 'features/reward/state/service-rewards-context';
import { getReward } from 'features/reward/utils/identity';

import { useRoute } from 'hooks/navigation/use-route';
import { selectors, useAppSelector } from 'state/global-state';
import { logger } from 'utils/logger';

import { IProductWizardParams, ProductWizardRewardState } from './types';

export const useProductWizardReward = (): ProductWizardRewardState => {
  const { params } = useRoute<IProductWizardParams>();
  const {
    cartIdEditing = '',
    loyaltyEngineId: loyaltyEngineIdParam,
    rewardBenefitId: rewardBenefitIdParam,
    rewardId: rewardIdParam,
  } = params || {};
  const { enabled: serviceRewardsEnabled } = useEnableLoyaltyServiceRewards();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedReward = cartIdEditing ? appliedRewards[cartIdEditing] : undefined;

  const hasRewardIntent = Boolean(
    loyaltyEngineIdParam || rewardIdParam || rewardBenefitIdParam || appliedReward
  );

  const directLoyaltyEngineId = serviceRewardsEnabled
    ? loyaltyEngineIdParam || appliedReward?.rewardId
    : undefined;
  const needsCmsCompatibilityLookup = Boolean(
    serviceRewardsEnabled && hasRewardIntent && !directLoyaltyEngineId && rewardIdParam
  );
  const {
    rewardsMap,
    loading: rewardsCatalogLoading,
    error: rewardsCatalogError,
  } = useServiceRewardsCatalog({ skip: !needsCmsCompatibilityLookup });
  const compatibilityReward =
    needsCmsCompatibilityLookup && rewardIdParam ? getReward(rewardsMap, rewardIdParam) : undefined;
  const loyaltyEngineId = directLoyaltyEngineId || compatibilityReward?.id;
  const mappedReward = loyaltyEngineId
    ? getReward(rewardsMap, loyaltyEngineId) ?? compatibilityReward
    : undefined;
  const singularReward = useLoyaltyServiceReward({
    loyaltyEngineId,
    fetchPolicy: 'cache-first',
    skip: !serviceRewardsEnabled || !loyaltyEngineId || Boolean(mappedReward),
  });
  const reward = mappedReward ?? singularReward.reward;

  useEffect(() => {
    if (serviceRewardsEnabled && singularReward.error) {
      logger.warn({
        error: singularReward.error,
        loyaltyEngineId,
        message: 'Failed to resolve loyalty-service Reward for product wizard',
      });
    }
  }, [loyaltyEngineId, serviceRewardsEnabled, singularReward.error]);

  useEffect(() => {
    if (needsCmsCompatibilityLookup && rewardsCatalogError) {
      logger.warn({
        error: rewardsCatalogError,
        rewardId: rewardIdParam,
        message: 'Failed to resolve CMS Reward route for product wizard',
      });
    }
  }, [needsCmsCompatibilityLookup, rewardIdParam, rewardsCatalogError]);

  if (serviceRewardsEnabled) {
    const compatibilityLoading = Boolean(
      needsCmsCompatibilityLookup && !compatibilityReward && rewardsCatalogLoading
    );
    const loading = Boolean(
      hasRewardIntent &&
        !reward &&
        (compatibilityLoading || (loyaltyEngineId && singularReward.loading))
    );
    const unavailable = Boolean(
      hasRewardIntent && !loading && (!loyaltyEngineId || !reward?.cmsId || !reward.rewardBenefitId)
    );

    return {
      hasRewardIntent,
      loading,
      unavailable,
      loyaltyEngineId,
      reward,
      rewardId: reward?.cmsId,
      rewardBenefitId: reward?.rewardBenefitId,
    };
  }

  return {
    hasRewardIntent,
    loading: false,
    unavailable: false,
    rewardId: rewardIdParam || appliedReward?.sanityId,
    rewardBenefitId: rewardBenefitIdParam || appliedReward?.rewardBenefitId,
  };
};
